import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { DropdownTriangle, Folder, SyncProgress, XCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { ConnectedAccount } from '@/settings/schemas/external-storage-schema'

interface MatterFolderSyncRowProps {
  account: ConnectedAccount
  canChooseFolder: boolean
  disconnecting?: boolean
  syncedFolderName: string | null
  onChooseFolder: () => void
  onDisconnectFolder: () => void
}

export function MatterFolderSyncRow({
  account,
  canChooseFolder,
  disconnecting = false,
  syncedFolderName,
  onChooseFolder,
  onDisconnectFolder,
}: MatterFolderSyncRowProps) {
  const providerLabel = kExternalStorageProviderDisplayName[account.provider]
  const isSynced = syncedFolderName != null
  const disableActions = !canChooseFolder || disconnecting

  return (
    <div className="flex min-h-[76px] items-center gap-4 border-t border-brand-neutral-200 px-4 pb-4 pt-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <Folder className="h-6 w-6 shrink-0 text-brand-neutral-400" />
          <p className="truncate text-base font-medium leading-6 tracking-[-0.32px] text-brand-neutral-950">{syncedFolderName ?? 'No folder synced yet'}</p>
        </div>
        {!isSynced && canChooseFolder && (
          <p className="mt-1 text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
            Choose which {providerLabel} folder to sync with this matter. Only one folder can be synced at a time.
          </p>
        )}
        {!canChooseFolder && (
          <p className="mt-1 text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">Reconnect this account in Settings to change synced folders.</p>
        )}
      </div>
      {isSynced ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              type="button"
              variant="plain"
              color="secondary"
              size="sm"
              className="shrink-0 gap-1"
              disabled={disableActions}
              leadingIcon={<SyncProgress className="h-4 w-4 shrink-0 text-brand-neutral-600" />}
              trailingIcon={<DropdownTriangle className="h-[18px] w-[18px] shrink-0 text-brand-neutral-950" />}
              aria-label={`Synced folder options: ${syncedFolderName}`}
            >
              Syncing
            </Button>
          </DropdownMenu.Trigger>
          <MenuContent align="end" className="min-w-[150px]">
            <MenuItemGroup>
              <MenuItem label="Choose Folder" icon={Folder} onSelect={onChooseFolder} variant="neutral" disabled={disableActions} />
              <MenuItem label="Disconnect" icon={XCircle} onSelect={onDisconnectFolder} variant="destructive" disabled={disableActions} />
            </MenuItemGroup>
          </MenuContent>
        </DropdownMenu.Root>
      ) : (
        <Button type="button" onClick={onChooseFolder} variant="outlined" color="primary" size="sm" className="shrink-0" disabled={!canChooseFolder}>
          Choose Folder
        </Button>
      )}
    </div>
  )
}
